import * as React from "react";

import { Stage } from "./Stage";
import { Box } from "@dropbox/dig-foundations";
import { Text } from "@dropbox/dig-components/typography";
import { SplitBox } from "../split-box";

import type { Atoms } from "@dropbox/dig-foundations";
import type { SplitBoxProps } from "../split-box";

export type StageAction = {
  element: React.ReactNode;
  onClick?: () => void; // When provided overrides any onClick already on the element
  type?: "next" | "prev" | "complete"; // When provided overrides any onClick already on the element
};

export type SplitStageParams = Omit<
  SplitBoxProps,
  "height" | "leftContent" | "rightContent"
> & {
  content: React.ReactNode;
  actionSide?: "left" | "right";
};

export interface StageParams {
  body: React.ReactNode;
  actions: Array<StageAction>;
  split?: SplitStageParams;
}

export interface StageStepperProps {
  /**
   * The current stage of the stepper.
   */
  steps: Array<StageParams>;

  /**
   * Callback function to be called when the stepper is complete.
   */
  onComplete: () => void;

  /**
   * Callback function to be called when the stepper step changes.
   */
  onChangeStep?: (step: number) => void;

  /**
   * Top padding.
   * @default "Micro Large"
   */
  paddingTop?: Atoms["paddingTop"];
  /**
   * Bottom padding.
   * @default "Micro Large"
   */
  paddingBottom?: Atoms["paddingBottom"];
  /**
   * Left padding.
   * @default "Micro Large"
   */
  paddingLeft?: Atoms["paddingLeft"];
  /**
   * Right padding.
   * @default "Micro Large"
   */
  paddingRight?: Atoms["paddingRight"];

  /**
   * Show the current step number. In the format "Step X of Y".
   * @default true
   */
  showStepNumber?: boolean;

  /**
   * The height of the stage stepper.
   * @default "fit-content"
   */
  height?: Atoms["height"];
}

/**
 * A component that displays a list of stages with a current step indicator.
 */
export const StageStepper: React.FC<StageStepperProps> = ({
  steps,
  onComplete,
  onChangeStep,
  paddingTop = "Macro Large",
  paddingBottom = "Macro Large",
  paddingLeft = "Macro Large",
  paddingRight = "Macro Large",
  showStepNumber = true,
  height = "fit-content",
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      onChangeStep?.(currentStep + 1);
      setCurrentStep((prev) => prev + 1);
    } else if (currentStep === steps.length - 1) {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      onChangeStep?.(currentStep - 1);
      setCurrentStep((prev) => prev - 1);
    }
  };

  const createActionWithNavigation = (action: StageAction): React.ReactNode => {
    const handleClick = () => {
      action.onClick?.();
      switch (action.type) {
        case "next":
          nextStep();
          break;
        case "prev":
          prevStep();
          break;
        case "complete":
          onComplete();
          break;
      }
    };

    if (!React.isValidElement(action.element)) {
      return action.element;
    }
    return React.cloneElement(action.element as React.ReactElement, {
      onClick: handleClick,
    });
  };

  const currentStage = steps[currentStep];
  const stageComponent = (
    <Box
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      paddingLeft={paddingLeft}
      paddingRight={paddingRight}
      height="100%"
      width="100%"
    >
      <Stage
        body={currentStage.body}
        primaryActions={currentStage.actions.map(createActionWithNavigation)}
        ternaryAction={
          showStepNumber ? (
            <Text color="subtle">
              {currentStep + 1} of {steps.length}
            </Text>
          ) : undefined
        }
        height={height}
      />
    </Box>
  );

  if (currentStage.split) {
    return (
      <SplitBox
        height="100%"
        {...currentStage.split}
        leftContent={
          currentStage.split.actionSide === "left"
            ? stageComponent
            : currentStage.split.content
        }
        rightContent={
          currentStage.split.actionSide === "right"
            ? stageComponent
            : currentStage.split.content
        }
      />
    );
  }

  return stageComponent;
};
